/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Focused hook for semantic surface colors only
 * Extracted from useDesignTokens for better performance and tree-shaking
 *
 * ARCHITECTURE: Reads colors directly from CSS custom properties (single source of truth)
 * This enables theme-aware components that respond to organization customizations
 */

import { getCSSVariable, hslToHex } from "@/components/ui/foundation/design-tokens";
import { useMemo } from "react";
import type {
  SemanticIntent,
  SemanticSurfaceColors,
  SemanticSurfaceShadeConfig,
  SurfaceContext,
  TokenShade,
} from "./types";

// Re-export shared types
export type {
  SemanticIntent,
  SemanticSurfaceColors,
  SemanticSurfaceShadeConfig,
  SurfaceContext,
} from "./types";

const contextualSemanticSurfaceShades: Record<
  SurfaceContext,
  Record<SemanticIntent, SemanticSurfaceShadeConfig>
> = {
  "on-light": {
    success: {
      text: 700,
      strongText: 800,
      mutedText: 600,
      background: 50,
      subtleBackground: 100,
      border: 200,
      icon: 500,
    },
    warning: {
      text: 700,
      strongText: 800,
      mutedText: 600,
      background: 50,
      subtleBackground: 100,
      border: 200,
      icon: 500,
    },
    error: {
      text: 700,
      strongText: 800,
      mutedText: 600,
      background: 50,
      subtleBackground: 100,
      border: 200,
      icon: 500,
    },
    info: {
      text: 700,
      strongText: 800,
      mutedText: 600,
      background: 50,
      subtleBackground: 100,
      border: 200,
      icon: 500,
    },
    primary: {
      text: 800,
      strongText: 900,
      mutedText: 700,
      background: 0,
      subtleBackground: 50,
      border: 200,
      icon: 600,
    },
    neutral: {
      text: 800,
      strongText: 900,
      mutedText: 600,
      background: 0,
      subtleBackground: 50,
      border: 200,
      icon: 500,
    },
  },
  "on-dark": {
    success: {
      text: 100,
      strongText: 50,
      mutedText: 200,
      background: 900,
      subtleBackground: 800,
      border: 700,
      icon: 400,
    },
    warning: {
      text: 100,
      strongText: 50,
      mutedText: 200,
      background: 900,
      subtleBackground: 800,
      border: 700,
      icon: 400,
    },
    error: {
      text: 100,
      strongText: 50,
      mutedText: 200,
      background: 900,
      subtleBackground: 800,
      border: 700,
      icon: 400,
    },
    info: {
      text: 100,
      strongText: 50,
      mutedText: 200,
      background: 900,
      subtleBackground: 800,
      border: 700,
      icon: 400,
    },
    primary: {
      text: 100,
      strongText: 50,
      mutedText: 200,
      background: 800,
      subtleBackground: 700,
      border: 600,
      icon: 400,
    },
    neutral: {
      text: 100,
      strongText: 50,
      mutedText: 200,
      background: 800,
      subtleBackground: 700,
      border: 600,
      icon: 400,
    },
  },
  "on-brand": {
    success: {
      text: 50,
      strongText: 0,
      mutedText: 100,
      background: 600,
      subtleBackground: 500,
      border: 400,
      icon: 200,
    },
    warning: {
      text: 50,
      strongText: 0,
      mutedText: 100,
      background: 600,
      subtleBackground: 500,
      border: 400,
      icon: 200,
    },
    error: {
      text: 50,
      strongText: 0,
      mutedText: 100,
      background: 600,
      subtleBackground: 500,
      border: 400,
      icon: 200,
    },
    info: {
      text: 50,
      strongText: 0,
      mutedText: 100,
      background: 600,
      subtleBackground: 500,
      border: 400,
      icon: 200,
    },
    primary: {
      text: 50,
      strongText: 0,
      mutedText: 100,
      background: 600,
      subtleBackground: 500,
      border: 400,
      icon: 200,
    },
    neutral: {
      text: 50,
      strongText: 0,
      mutedText: 100,
      background: 700,
      subtleBackground: 600,
      border: 500,
      icon: 300,
    },
  },
} as const;

/**
 * Get color value from CSS custom properties
 * Maps semantic intents to CSS variable names and returns hex colors
 */
function getIntentColorValue(
  intent: SemanticIntent,
  shade: TokenShade,
): string {
  // Map intent to CSS variable prefix
  const varPrefix = intent === "neutral" ? "muted" : intent;

  // Construct CSS variable name based on shade
  let varName: string;
  if (shade === 0) {
    varName = `--background`; // White/base
  } else if (shade === 500) {
    varName = `--${varPrefix}`; // Main color
  } else {
    varName = `--${varPrefix}-${shade}`;
  }

  // Read from CSS variable and convert to hex
  const hslValue = getCSSVariable(varName);
  return hslToHex(hslValue);
}

export interface UseSemanticSurfaceResult {
  getSemanticSurfaceColors: (
    intent: SemanticIntent,
    context?: SurfaceContext,
    overrides?: Partial<SemanticSurfaceShadeConfig>,
  ) => SemanticSurfaceColors;
}

/**
 * Focused hook for semantic surface colors only
 *
 * This is a lightweight alternative to useDesignTokens that only provides
 * semantic surface color functionality, reducing bundle size significantly.
 *
 * @example
 * ```tsx
 * const { getSemanticSurfaceColors } = useSemanticSurface();
 * const primarySurface = getSemanticSurfaceColors("primary");
 * ```
 */
export function useSemanticSurface(): UseSemanticSurfaceResult {
  return useMemo(() => {
    const getSemanticSurfaceColors = (
      intent: SemanticIntent,
      context: SurfaceContext = "on-light",
      overrides: Partial<SemanticSurfaceShadeConfig> = {},
    ): SemanticSurfaceColors => {
      // Validate intent and fallback to neutral for invalid intents
      const validIntent: SemanticIntent =
        intent &&
        typeof intent === "string" &&
        (intent === "primary" ||
          intent === "neutral" ||
          intent === "success" ||
          intent === "warning" ||
          intent === "error" ||
          intent === "info")
          ? intent
          : "neutral";

      const shades = {
        ...contextualSemanticSurfaceShades[context][validIntent],
        ...overrides,
      };

      return {
        text: getIntentColorValue(validIntent, shades.text),
        textStrong: getIntentColorValue(validIntent, shades.strongText),
        textMuted: getIntentColorValue(validIntent, shades.mutedText),
        background: getIntentColorValue(validIntent, shades.background),
        backgroundMuted: getIntentColorValue(
          validIntent,
          shades.subtleBackground,
        ),
        border: getIntentColorValue(validIntent, shades.border),
        icon: getIntentColorValue(validIntent, shades.icon),
      };
    };

    return {
      getSemanticSurfaceColors,
    };
  }, []);
}
